import * as React from "react";
import { CgChevronDown, CgProfile } from "react-icons/cg";
import { IconBaseProps } from "react-icons/lib";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardImg,
  CardTitle,
  Container,
} from "reactstrap";
import "styles/session-panel.scss";
import Overlay from "./Overlay";

export interface IActionButton {
  action?: () => void;
  text: string;
  isDisabled?: boolean;
  icon?: IconBaseProps;
  isNavigation?: boolean;
}

const SessionPanel: React.FC<{
  title?: string;
  image?: string;
  subtitle?: string | React.ReactElement;
  onGoBack?: () => void;
  onClose?: () => void;
  footerNote?: string | React.ReactElement;
  primaryButton?: IActionButton;
  secondaryButton?: IActionButton;
  additionalSecondaryButton?: IActionButton;
  tertiaryButton?: IActionButton;
  showPoweredBy?: boolean;
  isLoading?: boolean;
  isDisplayCentered?: boolean;
  rootLifeName?: string;
  hideBackground?: boolean;
  onScroll?: (e: React.UIEvent<HTMLDivElement, UIEvent>) => void;
  customButtons?: React.ReactElement;
}> = ({
  title,
  image,
  subtitle,
  onGoBack,
  onClose,
  footerNote,
  primaryButton,
  secondaryButton,
  tertiaryButton,
  additionalSecondaryButton,
  children,
  isLoading,
  isDisplayCentered,
  rootLifeName,
  hideBackground,
  onScroll,
  customButtons,
}) => {
  const displayFooter =
    secondaryButton ||
    primaryButton ||
    additionalSecondaryButton ||
    tertiaryButton ||
    customButtons ||
    footerNote;
  return (
    <Container
      className={`d-flex justify-content-center session-panel ${
        !displayFooter ? "no-footer" : ""
      }`}
    >
      <Card className="session-panel-card justify-content-between">
        {isLoading && <Overlay showLoading isSessionLoading />}
        <div
          className="d-flex "
          style={{ height: 50, padding: "20px 32px 0px 32px" }}
        >
          {onGoBack && (
            <em
              style={{ cursor: "pointer" }}
              onClick={onGoBack}
              className="fas fa-arrow-left me-auto"
            />
          )}
          {onClose && (
            <em
              style={{ cursor: "pointer" }}
              onClick={onClose}
              className="fas fa-times ms-auto"
            />
          )}
        </div>
        <div
          onScroll={(e) => (onScroll ? onScroll(e) : {})}
          className={`session-panel-content ${
            isDisplayCentered
              ? "d-flex justify-content-center align-items-center flex-column"
              : ""
          } ${hideBackground ? "" : "session-panel-background"}`}
        >
          {(title || subtitle || image || rootLifeName) && (
            <CardHeader className="px-0 w-100">
              {image && (
                <>
                  {image.includes("/logos/") ? (
                    <div className="border border-secondary rounded-3 mb-3 session-panel-logo-container">
                      <CardImg
                        src={image}
                        alt="logo"
                        className="session-panel-logo"
                      />
                    </div>
                  ) : (
                    <CardImg
                      src={image}
                      alt="logo"
                      className="session-panel-default-logo my-3"
                    />
                  )}
                </>
              )}

              {rootLifeName && (
                <Badge
                  className="text-center d-flex justify-content-center align-items-center m-auto mb-2"
                  style={{ width: "fit-content" }}
                  color="lightCloud"
                  pill
                >
                  <CgProfile size="15px" className="mx-1"></CgProfile>
                  <span className="mx-1" style={{ fontSize: 14 }}>
                    {rootLifeName}
                  </span>
                </Badge>
              )}
              <CardTitle>
                <h4 className="text-center session-panel-title">{title}</h4>
                <h6 className="text-center session-panel-subtitle">
                  {subtitle}
                </h6>
              </CardTitle>
            </CardHeader>
          )}
          <CardBody
            className="p-0 w-100"
            style={{ flex: isDisplayCentered ? "unset" : "1 1 auto" }}
          >
            <div style={{ flex: isDisplayCentered ? "unset" : "1 1 auto" }}>
              {children}
            </div>
          </CardBody>
        </div>
        {hideBackground && (
          <div className="scroll-shadow">
            <CgChevronDown size={65} />
          </div>
        )}
        {displayFooter && (
          <CardFooter>
            <div className="d-flex flex-column ">
              {footerNote && <div className="text-center"> {footerNote} </div>}
              {primaryButton && (
                <Button
                  color="primary"
                  className="w-100"
                  onClick={primaryButton.action}
                  disabled={primaryButton.isDisabled ?? false}
                >
                  {(!primaryButton.isNavigation && primaryButton.icon) ??
                    primaryButton.icon}
                  {primaryButton.text}{" "}
                  {(!!primaryButton.isNavigation && primaryButton.icon) ??
                    primaryButton.icon}
                </Button>
              )}
              {secondaryButton && (
                <Button
                  className="text-decoration-none w-100 session-panel-btn-secondary"
                  style={{ marginTop: 12 }}
                  color="primary"
                  outline
                  onClick={secondaryButton.action}
                  disabled={secondaryButton.isDisabled ?? false}
                >
                  {secondaryButton.icon ?? secondaryButton.icon}
                  <strong>{secondaryButton.text}</strong>
                </Button>
              )}
              {additionalSecondaryButton && (
                <Button
                  className="text-decoration-none w-100 session-panel-btn-secondary"
                  style={{ marginTop: 12 }}
                  color="primary"
                  outline
                  onClick={additionalSecondaryButton.action}
                  disabled={additionalSecondaryButton.isDisabled ?? false}
                >
                  {additionalSecondaryButton.icon ??
                    additionalSecondaryButton.icon}
                  <strong>{additionalSecondaryButton.text}</strong>
                </Button>
              )}
              {tertiaryButton && (
                <Button
                  className="text-decoration-none w-100 btn-link"
                  style={{ marginTop: 12 }}
                  color="primary"
                  outline
                  onClick={tertiaryButton.action}
                  disabled={tertiaryButton.isDisabled ?? false}
                >
                  {tertiaryButton.icon ?? tertiaryButton.icon}
                  <strong>{tertiaryButton.text}</strong>
                </Button>
              )}
              {customButtons}
            </div>
          </CardFooter>
        )}
      </Card>
    </Container>
  );
};

export default SessionPanel;
